import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import ImageUploader from './ImageUploader';
import ComponentsCollectionEditor from './ComponentsCollectionEditor';
import { get } from './httpClient';

/**
 * Common fields for Page editors: Slug, Title, and Preview image.
 * Props:
 *  - form: object with keys { slug, title, preview, description, caption }
 *  - setForm: setState function to update form
 *  - websiteId: id required by ImageUploader
 *  - slugPlaceholder?: optional placeholder for slug input
 *  - titlePlaceholder?: optional placeholder for title input
 */
function slugify(value) {
  const s = String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[\s_]+/g, '-') // spaces/underscores -> hyphen
    .replace(/[^a-z0-9-]/g, '') // remove non-allowed
    .replace(/-+/g, '-') // collapse hyphens
    .replace(/^-+|-+$/g, ''); // trim hyphens
  return s;
}

function isValidSlug(s) {
  const v = String(s || '').trim();
  if (!v) return false;
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(v);
}

// Returns { pct, status, label } for a character-count meter
function titleMeter(len) {
  const max = 60;
  if (len === 0) return { pct: 0, status: 'empty', label: '0 / 60' };
  if (len <= 60) {
    const good = len >= 30;
    return { pct: Math.min((len / max) * 100, 100), status: good ? 'good' : 'warn', label: `${len} / 60` };
  }
  return { pct: 100, status: 'error', label: `${len} / 60` };
}

function descMeter(len) {
  const max = 160;
  if (len === 0) return { pct: 0, status: 'empty', label: '0 / 160' };
  if (len <= 160) {
    const good = len >= 140;
    return { pct: Math.min((len / max) * 100, 100), status: good ? 'good' : 'warn', label: `${len} / 160` };
  }
  // over 160: show overflow in red, cap bar at 100%
  return { pct: 100, status: 'error', label: `${len} / 160` };
}

function titleHint(len) {
  if (len === 0) return 'Aim for 50–60 characters for best display in search results';
  if (len < 30) return 'Too short — add more detail to improve click-through';
  if (len <= 60) return 'Good length';
  if (len <= 70) return 'Slightly long — may be truncated in some search results';
  return 'Too long — will be cut off in search results';
}

function descHint(len) {
  if (len === 0) return 'Every page needs a unique 140–160 character description';
  if (len < 100) return 'Too short — a good description improves click-through rate';
  if (len <= 160) return 'Good length';
  if (len <= 180) return 'Slightly long — may be trimmed by search engines';
  return 'Too long — search engines will truncate this description';
}

const SEO_TIPS = {
  slug: [
    'Use lowercase letters, numbers, and hyphens only — no spaces or special characters.',
    'Keep it short and descriptive: 2–5 words is ideal.',
    'Include your primary keyword naturally.',
    'Avoid stop words like "a", "the", "and" to keep URLs concise.',
    'Example: best-running-shoes, not the-best-running-shoes-for-2024',
  ],
  title: [
    'Place your primary keyword near the beginning of the title.',
    'Keep it under 60 characters so it doesn\'t get cut off in search results.',
    'Every page must have a unique title — never duplicate across pages.',
    'Write for humans first: make it compelling, not just keyword-stuffed.',
    'Include your brand name at the end if space allows: "Product | Brand".',
  ],
  description: [
    'Write 140–160 characters — enough to summarize the page without being truncated.',
    'Include your primary keyword naturally at least once.',
    'Treat it like ad copy: give users a reason to click your link.',
    'Every page needs a unique description — duplicates hurt rankings.',
    'Add a subtle call to action: "Learn more", "Shop now", "Find out how".',
  ],
  preview: [
    'This image appears when the page is shared on social media (Open Graph).',
    'Recommended size: 1200 × 630 px (1.91:1 ratio).',
    'Use high-contrast visuals — it competes with dozens of other posts in a feed.',
    'Include readable text overlay if the image needs context.',
    'Avoid small logos on white backgrounds — they disappear in social feeds.',
  ],
  caption: [
    'A short visible label shown to describe the page',
    'Keep it concise — one sentence or a brief phrase works best.',
    'It can be displayed in the menu or in the catalog or the gallery.',
  ],
  image: [
    'Upload a preview image for this page (Open Graph / social sharing).',
    'Recommended size: 1200 × 630 px (1.91:1 ratio).',
    'Use high-contrast visuals — it competes with dozens of other posts in a feed.',
    'Avoid small logos on white backgrounds — they disappear in social feeds.',
  ],
};

function SeoTip({ field }) {
  const tips = SEO_TIPS[field] || [];
  return (
    <span className="seo-tip">
      <span className="seo-tip__icon" aria-label="SEO tips">ⓘ</span>
      <span className="seo-tip__popup" role="tooltip">
        <span className="seo-tip__title">Best practices</span>
        <ul className="seo-tip__list">
          {tips.map((t, i) => <li key={i}>{t}</li>)}
        </ul>
      </span>
    </span>
  );
}

function SeoMeter({ meter, hint }) {
  return (
    <div className="seo-meter">
      <div className="seo-meter__track">
        <div
          className={`seo-meter__fill seo-meter__fill--${meter.status}`}
          style={{ width: `${meter.pct}%` }}
        />
      </div>
      <span className={`seo-meter__label seo-meter__label--${meter.status}`}>
        {meter.status === 'empty' ? hint : `${meter.label} · ${hint}`}
      </span>
    </div>
  );
}

function SerpPreview({ title, description, slug, domain }) {
  const host = domain || 'yoursite.com';
  const displayUrl = slug ? `${host} › ${slug}` : host;
  const hasTitle = title && title.trim();
  const hasDesc = description && description.trim();

  return (
    <div className="seo-preview">
      <div className="seo-preview__chrome">
        <div className="seo-preview__dots">
          <div className="seo-preview__dot" />
          <div className="seo-preview__dot" />
          <div className="seo-preview__dot" />
        </div>
        <div className="seo-preview__bar">{displayUrl}</div>
      </div>
      <div className="seo-preview__result">
        <div className="seo-preview__breadcrumb">
          <div className="seo-preview__favicon" />
          <span className="seo-preview__domain">{host}</span>
          {slug && <><span>›</span><span className="seo-preview__slug">{slug}</span></>}
        </div>
        <div className={`seo-preview__title${hasTitle ? '' : ' seo-preview__title--empty'}`}>
          {hasTitle || 'Page title will appear here'}
        </div>
        <div className={`seo-preview__desc${hasDesc ? '' : ' seo-preview__desc--empty'}`}>
          {hasDesc || 'Meta description will appear here. Write a compelling summary that makes people want to click your link in search results.'}
        </div>
      </div>
    </div>
  );
}

function PageBasicFields({ form, setForm, websiteId, slugPlaceholder = 'e.g. products', titlePlaceholder = 'Enter title', preserveEmptySlug = false }) {
  const slugTouchedManually = useRef(false);
  const lastAutoSlugRef = useRef('');
  const [websiteDomain, setWebsiteDomain] = useState('');

  useEffect(() => {
    if (!websiteId) return;
    let mounted = true;
    get(`websites/${websiteId}`).then(resp => {
      if (mounted) setWebsiteDomain(resp?.website?.domain || resp?.domain || '');
    }).catch(() => {});
    return () => { mounted = false; };
  }, [websiteId]);

  // When title changes from outside (prefilled or programmatic) and slug is empty & untouched, set an auto slug
  useEffect(() => {
    if (!preserveEmptySlug && (form.slug || '').trim() === '' && !slugTouchedManually.current) {
      const auto = slugify(form.title);
      lastAutoSlugRef.current = auto;
      if (auto) {
        setForm(prev => ({ ...prev, slug: auto }));
      }
    }
    // We intentionally depend on form.title and form.slug to react to preloaded data
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.title, form.slug, preserveEmptySlug]);

  const titleLen = (form.title || '').length;
  const descLen = (form.description || '').length;
  const tMeter = titleMeter(titleLen);
  const dMeter = descMeter(descLen);

  return (
    <>
      <div className="props-panel">
        <h6 className="theme-group-label mb-3">SEO</h6>
        <div className="seo-fields">
          <div className="seo-field-row">
            <Form.Label>Link <SeoTip field="slug" /></Form.Label>
            <div className="seo-field-wrap">
              <Form.Control
                type="text"
                value={form.slug}
                isInvalid={(form.slug || '').trim() !== '' && !isValidSlug(form.slug)}
                onChange={(e) => {
                  slugTouchedManually.current = true;
                  setForm({ ...form, slug: e.target.value });
                }}
                onBlur={(e) => {
                  const sanitized = slugify(e.target.value);
                  lastAutoSlugRef.current = sanitized;
                  setForm(prev => ({ ...prev, slug: sanitized }));
                }}
                placeholder={slugPlaceholder}
              />
            </div>
          </div>

          <div className="seo-field-full">
            <Form.Label>Title <SeoTip field="title" /></Form.Label>
            <Form.Control
              type="text"
              value={form.title}
              onChange={(e) => {
                const nextTitle = e.target.value;
                setForm(prev => {
                  const next = { ...prev, title: nextTitle };
                  if (!preserveEmptySlug && (!slugTouchedManually.current || (prev.slug || '') === (lastAutoSlugRef.current || ''))) {
                    const auto = slugify(nextTitle);
                    lastAutoSlugRef.current = auto;
                    next.slug = auto;
                  }
                  return next;
                });
              }}
              placeholder={titlePlaceholder}
            />
            <SeoMeter meter={tMeter} hint={titleHint(titleLen)} />
          </div>

          <div className="seo-field-full">
            <Form.Label>Description <SeoTip field="description" /></Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.description || ''}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Enter SEO description"
            />
            <SeoMeter meter={dMeter} hint={descHint(descLen)} />
          </div>

          <SerpPreview
            title={form.title}
            description={form.description}
            slug={form.slug}
            domain={websiteDomain}
          />
        </div>
      </div>

      <div className="props-panel">
        <h6 className="theme-group-label mb-3">Preview</h6>
        <div className="seo-fields">
          <div className="seo-field-full">
            <Form.Label>Caption <SeoTip field="caption" /></Form.Label>
            <Form.Control
              type="text"
              value={form.caption || ''}
              onChange={(e) => setForm(prev => ({ ...prev, caption: e.target.value }))}
              placeholder="Enter caption"
            />
          </div>

          <div className="seo-field-row" style={{ alignItems: 'flex-start' }}>
            <Form.Label style={{ paddingTop: 0 }}>Image <SeoTip field="image" /></Form.Label>
            <div className="seo-field-wrap" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 200, flexShrink: 0 }}>
                <ImageUploader type="thumb" websiteId={websiteId} onUploaded={(resp) => { setForm({ ...form, preview: resp.url }); }} />
              </div>
              {form.preview && <img src={form.preview} alt="Preview" className="img-preview" />}
            </div>
          </div>
        </div>
      </div>

      <div className="props-panel">
        <h6 className="theme-group-label mb-3">Components</h6>
        <ComponentsCollectionEditor
          websiteId={websiteId}
          value={form.components || { items: [] }}
          onChange={(val) => {
            setForm(prev => ({
              ...prev,
              components: val,
            }));
          }}
        />
      </div>
    </>
  );
}

export default PageBasicFields;
